import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Copy, Landmark } from "lucide-react";
import { api, errMsg, fmtDate, today } from "@/lib/api";
import { useMeta } from "@/pages/Services";
import { Screen, Card, Chip, Empty, rs } from "./ui";
import { BalanceCard, Label, Field, Picker, Chips, Radio, CTA } from "./kit";

const shrink = (file) => new Promise((resolve, reject) => {
  if (!file.type.startsWith("image/")) {
    if (file.size > 2e6) return reject(new Error("Max 2MB file"));
    const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(file); return undefined;
  }
  const img = new Image();
  img.onload = () => {
    const k = Math.min(1, 1400 / Math.max(img.width, img.height));
    const c = document.createElement("canvas"); c.width = img.width * k; c.height = img.height * k;
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    resolve(c.toDataURL("image/jpeg", 0.75));
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
  return undefined;
});

export function MFundRequest() {
  const meta = useMeta();
  const [banks, setBanks] = useState([]);
  const blank = { bank_id: "", amount: "", utr: "", payment_mode: "", deposit_date: today(), remarks: "", slip: "" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/company-banks").then(({ data }) => setBanks(data)).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const bank = banks.find((b) => b.id === f.bank_id);
  const onFile = async (e) => { const file = e.target.files?.[0]; if (!file) return; try { setF({ ...f, slip: await shrink(file) }); } catch (err) { toast.error(err.message || "Could not read file"); } };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/fund-requests", { ...f, amount: Number(f.amount) }); toast.success(`Request ${data.req_id} submitted`); setF(blank); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Screen title="Add Money" dark testid="m-fund" right={<Link to="/app/fund-history" className="px-2 text-xs font-bold text-white/80" data-testid="m-fund-history-link">History</Link>}>
      <form onSubmit={submit} data-testid="m-fund-form">
        <BalanceCard />
        <Label>Enter Amount</Label>
        <Field prefix="₹" type="number" inputMode="decimal" value={f.amount} onChange={set("amount")} required className="text-xl font-semibold" data-testid="m-fr-amount" />
        <Chips values={[500, 1000, 5000, 10000]} value={f.amount} onPick={(v) => setF({ ...f, amount: v })} testid="m-fr-chips" />
        <Label>Deposited In</Label>
        <div className="space-y-2" data-testid="m-fr-bank">
          {!banks.length && <div className="rounded-xl bg-slate-100 p-4 text-sm text-slate-500">No company bank available. Contact admin.</div>}
          {banks.map((b) => (
            <button type="button" key={b.id} onClick={() => setF({ ...f, bank_id: b.id })} className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left ${f.bank_id === b.id ? "border-[#F26B1D]" : "border-slate-200"}`} data-testid={`m-fr-bank-${b.id}`}>
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-slate-100"><Landmark className="h-5 w-5" /></span>
              <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{b.bank}</span><span className="block truncate font-mono text-xs text-slate-500">{b.account_no} · {b.ifsc}</span></span>
              <Radio on={f.bank_id === b.id} />
            </button>
          ))}
        </div>
        {bank && <button type="button" onClick={() => { navigator.clipboard?.writeText(bank.account_no); toast.success("Account number copied"); }} className="mt-2 flex items-center gap-1 text-xs font-semibold text-[#F26B1D]" data-testid="m-fr-copy"><Copy className="h-3.5 w-3.5" />Copy account number ({bank.account_name})</button>}
        <Label>Payment Mode</Label>
        <Picker value={f.payment_mode} onChange={set("payment_mode")} required options={[{ value: "", label: "Select mode" }, ...(meta?.payment_modes || [])]} data-testid="m-fr-mode" />
        <Label>UTR / Reference No.</Label>
        <Field value={f.utr} onChange={set("utr")} required className="font-mono" data-testid="m-fr-utr" />
        <Label>Deposit Date</Label>
        <Field type="date" value={f.deposit_date} onChange={set("deposit_date")} required data-testid="m-fr-date" />
        <Label>Payment Slip</Label>
        <label className="flex h-24 cursor-pointer items-center justify-center gap-3 rounded-xl border-2 border-dashed border-slate-200 text-sm font-semibold text-slate-600" data-testid="m-fr-slip">
          {f.slip ? (f.slip.startsWith("data:image") ? <img src={f.slip} alt="slip" className="h-20 rounded-lg object-contain" /> : "File attached") : <><Camera className="h-6 w-6 text-[#F26B1D]" />Camera / Gallery</>}
          <input type="file" accept="image/*,.pdf" className="hidden" onChange={onFile} data-testid="m-fr-slip-input" />
        </label>
        <Label>Remarks (optional)</Label>
        <Field value={f.remarks} onChange={set("remarks")} data-testid="m-fr-remarks" />
        <CTA type="submit" loading={busy} disabled={!f.bank_id} data-testid="m-fr-submit">Proceed</CTA>
      </form>
    </Screen>
  );
}

export function MFundHistory() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/fund-requests").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const cancel = async (id) => { try { await api.post(`/fund-requests/${id}/cancel`); toast.success("Cancelled"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Screen title="Fund Request History" testid="m-fund-history">
      {rows && !rows.length && <Empty text="No fund requests yet" />}
      <div className="space-y-3">
        {(rows || []).map((r) => (
          <Card key={r.id} data-testid={`m-fr-${r.req_id}`}>
            <div className="flex items-start justify-between">
              <div><div className="font-mono text-lg font-bold">{rs(r.amount)}</div><div className="text-xs text-slate-500">{r.req_id} · {fmtDate(r.created_at)}</div></div>
              <Chip s={r.status} testid={`m-fr-status-${r.req_id}`} />
            </div>
            <div className="mt-2 text-xs text-slate-600">UTR <span className="font-mono">{r.utr}</span> · {r.payment_mode}</div>
            {r.admin_remarks && <div className="mt-2 rounded-xl bg-slate-50 p-2 text-xs" data-testid={`m-fr-remarks-${r.req_id}`}>Admin: {r.admin_remarks}</div>}
            {r.status === "PENDING" && <button onClick={() => cancel(r.id)} className="mt-2 text-xs font-bold text-red-600" data-testid={`m-fr-cancel-${r.req_id}`}>Cancel request</button>}
          </Card>
        ))}
      </div>
    </Screen>
  );
}
